import React, { useState } from "react";
import { MuiBox, MuiTypography } from "components/atoms";
import FileMenu from "../FileMenu";
import dayjs from "dayjs";
import { getFileType } from "utils/getFileType";
import { useSelector } from "react-redux";
// import SmartImage from "components/atoms/SmartImage";
// import DocImg from "assets/images/svg-icons/docFileIcon.svg";
import { useLocation } from "react-router-dom";
import useCachedImage from "hooks/useCachedImage";
import LinkIcon from "assets/images/svg-icons/linkIcon.svg";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/hi";
import "dayjs/locale/gu";
import { useTranslation } from "react-i18next";
import StarFillIcon from "assets/images/svg-icons/starFill.svg";
import TrashIcon from "assets/images/svg-icons/trashRed.svg";

const FileCard = ({
  page,
  item,
  onSelect,
  selected,
  onDoubleClick,
  clearSelection,
  isStarList,
  selectedItems,
}) => {
  const [imageError, setImageError] = useState(false);
  const location = useLocation();
  const { drivePrefrences } = useSelector((state) => state.profile);
  const [contextMenu, setContextMenu] = useState(null);
  const { i18n } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const disableAnimation = user?.default_animation === true || "true";
  const isStarPage = location.pathname.includes("/drive/starred");
  const isSharePage = location.pathname.includes("/drive/sharelink");

  const handleContextMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const imageTypes = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "svg",
    "heif",
    "heic",
    "tiff",
    "dng",
    "bmp",
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".heif",
    ".heic",
    ".tiff",
    ".dng",
    ".bmp",
  ];

  const videoTypes = [
    "mp4",
    "mov",
    "avi",
    "mkv",
    "wmv",
    "flv",
    "webm",
    "m4v",
    "3gp",
    "mpeg",
    "mpg",
    "mts",
    "m2ts",
    "ogv",
    "vob",
    "rm",
    "rmvb",
    "divx",
    "asf",
    ".mp4",
    ".mov",
    ".avi",
    ".mkv",
    ".wmv",
    ".flv",
    ".webm",
    ".m4v",
    ".3gp",
    ".mpeg",
    ".mpg",
    ".mts",
    ".m2ts",
    ".ogv",
    ".vob",
    ".rm",
    ".rmvb",
    ".divx",
    ".asf",
  ];

  const isVideo = videoTypes.includes(
    item?.file_type?.replace(".", "").toLowerCase(),
  );

  const isImage = imageTypes.includes(item?.file_type);
  const hasThumbnail = !!item?.view_url;

  const MAX_THUMBNAIL_SIZE = 100 * 1024 * 1024; // 10 MB

  const isLargeFile = Number(item?.file_size || 0) > MAX_THUMBNAIL_SIZE;

  // Reset error when item changes
  React.useEffect(() => {
    setImageError(false);
  }, [item?.id]);

  const formatFileSize = (bytes) => {
    const size = Number(bytes);

    if (size < 1024) {
      return size === 1 ? "1 Byte" : `${size} Bytes`;
    }

    const KB = size / 1024;
    if (KB < 1024) {
      return `${KB.toFixed(2)} KB`;
    }

    const MB = KB / 1024;
    if (MB < 1024) {
      return `${MB.toFixed(2)} MB`;
    }

    const GB = MB / 1024;
    return `${GB.toFixed(2)} GB`;

  };

  const sizeLabel = formatFileSize(item?.file_size);
  const isSecure = Boolean(
    (item?.is_secure || // My Files
      item?.file?.is_secure || // Favourite Items
      item?.folder?.is_secure) &&
    drivePrefrences?.drive_secure_enabled === "true", // Shared Items / Folders
  );
  // Determine if we should show preview (image with valid URL and no error)
  const showPreview =
    (isImage || isVideo) &&
    hasThumbnail &&
    !imageError &&
    !isSecure &&
    !isLargeFile;

  // Show preview only when file is not secure

  const { src: cachedThumbSrc } = useCachedImage(
    showPreview ? item?.thumbnail_url : null,
  );

  return (
    <MuiBox
      sx={{
        backgroundColor: selected ? "background.blue400" : "background.blue200",
        borderRadius: "8px",
        p: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        cursor: "pointer",
        transition: disableAnimation
          ? "none"
          : "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
        ...(disableAnimation
          ? {
            "&:hover": {
              backgroundColor: "background.blue300",
            },
          }
          : {
            "&:hover": {
              transform: "translateY(-2px)",
              backgroundColor: "background.blue300",
              boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.10)",
            },
          }),
      }}
      onClick={onSelect}
      onContextMenu={handleContextMenu}
    >
      <MuiBox
        sx={{
          height: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          borderRadius: "8px",
        }}
        onDoubleClick={onDoubleClick}
      >
        {showPreview && item?.thumbnail_url ? (
          cachedThumbSrc ? (
            <img
              src={cachedThumbSrc}
              alt={item?.file_name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={() => setImageError(true)}
              onLoad={() => setImageError(false)}
            />
          ) : (
            // thumbResolving === true here; render nothing or a skeleton if you have one
            <MuiBox sx={{ width: "100%", height: "100%" }} />
          )
        ) : (
          <img
            src={getFileType(item?.file_type)}
            alt=""
            style={{
              width: "80px",
              height: "80px",
              objectFit: "contain",
            }}
          />
        )}
      </MuiBox>
      <MuiBox
        sx={{
          p: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <MuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
          onDoubleClick={onDoubleClick}
        >
          <MuiTypography
            fontWeight="500"
            variant="14"
            color="text.darkBlack"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item?.file_name}
          </MuiTypography>
          <MuiBox sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {item?.is_starred && !isStarPage && !isStarList && (
              <img
                src={StarFillIcon}
                alt="folderIcon"
                className="img-contain"
                style={{ width: "14px", height: "14px", marginRight: "4px" }}
              />
            )}
            {item?.is_trashed &&
              location.pathname === "/drive/settings/storage-page" && (
                <img
                  src={TrashIcon}
                  alt="folderIcon"
                  className="img-contain"
                  style={{ width: "14px", height: "14px", marginRight: "4px" }}
                />
              )}
            <MuiTypography fontWeight="500" variant="12" color="text.gray1400">
              {location.pathname === "/drive/settings/storage-page"
                ? sizeLabel
                : dayjs(Number(item?.updated_at))
                  .locale(i18n.language)
                  .format("MMMM D, YYYY")}
            </MuiTypography>
          </MuiBox>
        </MuiBox>
        {item?.general_access?.visibility === "public" && (
          <MuiBox sx={{ height: "20px", width: "20px" }}>
            <img src={LinkIcon} alt="linkIcon" className="img-contain" />
          </MuiBox>
        )}
        {!isSharePage && (
          <FileMenu
            page={page}
            item={item}
            clearSelection={clearSelection}
            selectedItems={selectedItems}
            contextMenu={contextMenu}
            setContextMenu={setContextMenu}
          />
        )}
      </MuiBox>
    </MuiBox>
  );
};

export default FileCard;
